import {
  Box,
  Flex,
  FormControl,
  FormLabel,
  Select,
  SimpleGrid,
  Text,
  useColorModeValue,
} from '@chakra-ui/react';
import MiniStatistics from 'components/card/MiniStatistics';
import IconBox from 'components/icons/IconBox';
import {
  CartIcon,
  CreditIcon,
  DocumentIcon,
  RocketIcon,
  StatsIcon,
  WalletIcon,
} from 'components/icons/Icons';
import Card from 'components/card/Card';
import ExpensePieCard from 'components/finance/ExpensePieCard';
import FinanceTotalSpent from 'components/finance/dashboard/FinanceTotalSpent';
import FinanceMonthlyRevenue from 'components/finance/dashboard/FinanceMonthlyRevenue';
import FinanceDailyExpenses from 'components/finance/dashboard/FinanceDailyExpenses';
import FinanceRecentTable from 'components/finance/dashboard/FinanceRecentTable';
import { formatAmount, monthLabel } from 'lib/finance';
import { useFinance } from 'contexts/FinanceContext';

export default function Dashboard() {
  const brandColor = useColorModeValue('brand.500', 'white');
  const boxBg = useColorModeValue('secondaryGray.300', 'whiteAlpha.100');
  const textColor = useColorModeValue('secondaryGray.900', 'white');
  const textColorSecondary = useColorModeValue('secondaryGray.600', 'gray.400');
  const {
    totals,
    categories,
    currencySymbol,
    filters,
    setFilters,
    availableMonthKeys,
  } = useFinance();

  const periodLabel =
    !filters.month || filters.month === 'all'
      ? 'All time'
      : monthLabel(filters.month);
  const isCurrentMonth =
    filters.month &&
    filters.month !== 'all' &&
    filters.month === new Date().toISOString().slice(0, 7);

  const incomeName = isCurrentMonth
    ? 'Income this month'
    : filters.month === 'all'
      ? 'Total income'
      : `Income · ${periodLabel}`;
  const expenseName = isCurrentMonth
    ? 'Expense this month'
    : filters.month === 'all'
      ? 'Total expense'
      : `Expense · ${periodLabel}`;

  const savingsRate =
    totals.totalIncome > 0
      ? `+${Math.round((totals.balance / totals.totalIncome) * 100)}%`
      : '0%';

  return (
    <Box pt={{ base: '130px', md: '80px', xl: '80px' }}>
      <Card mb="20px" p="20px">
        <Flex
          align={{ base: 'stretch', md: 'center' }}
          justify="space-between"
          direction={{ base: 'column', md: 'row' }}
          gap="12px"
        >
          <Box>
            <Text color={textColor} fontSize="22px" fontWeight="700" mb="4px">
              Dashboard
            </Text>
            <Text color={textColorSecondary} fontSize="sm" fontWeight="500">
              Showing {periodLabel.toLowerCase()} income & expenses
            </Text>
          </Box>
          <FormControl maxW={{ base: '100%', md: '240px' }}>
            <FormLabel
              fontSize="xs"
              fontWeight="600"
              color={textColorSecondary}
              mb="6px"
            >
              Period
            </FormLabel>
            <Select
              value={filters.month || 'all'}
              onChange={(e) => setFilters({ month: e.target.value })}
              borderRadius="16px"
              bg={boxBg}
              h="44px"
              fontWeight="600"
            >
              <option value="all">All months</option>
              {availableMonthKeys.map((key) => (
                <option key={key} value={key}>
                  {monthLabel(key)}
                </option>
              ))}
            </Select>
          </FormControl>
        </Flex>
      </Card>

      <SimpleGrid
        columns={{ base: 1, md: 2, lg: 3, xl: 3 }}
        gap="20px"
        mb="20px"
      >
        <MiniStatistics
          startContent={
            <IconBox
              w="56px"
              h="56px"
              bg={boxBg}
              icon={<StatsIcon color={brandColor} w="24px" h="24px" />}
            />
          }
          name={incomeName}
          value={`${currencySymbol}${formatAmount(totals.totalIncome)}`}
        />
        <MiniStatistics
          startContent={
            <IconBox
              w="56px"
              h="56px"
              bg={boxBg}
              icon={<CreditIcon color={brandColor} w="24px" h="24px" />}
            />
          }
          name={expenseName}
          value={`${currencySymbol}${formatAmount(totals.totalExpense)}`}
        />
        <MiniStatistics
          startContent={
            <IconBox
              w="56px"
              h="56px"
              bg={boxBg}
              icon={<RocketIcon color={brandColor} w="24px" h="24px" />}
            />
          }
          growth={savingsRate}
          name="Savings"
          value={`${currencySymbol}${formatAmount(Math.max(totals.balance, 0))}`}
        />
        <MiniStatistics
          startContent={
            <IconBox
              w="56px"
              h="56px"
              bg={boxBg}
              icon={<WalletIcon color={brandColor} w="24px" h="24px" />}
            />
          }
          name="Your balance"
          value={`${currencySymbol}${formatAmount(totals.balance)}`}
        />
        <MiniStatistics
          startContent={
            <IconBox
              w="56px"
              h="56px"
              bg={boxBg}
              icon={<CartIcon color={brandColor} w="24px" h="24px" />}
            />
          }
          name="Transactions"
          value={totals.transactionCount}
        />
        <MiniStatistics
          startContent={
            <IconBox
              w="56px"
              h="56px"
              bg={boxBg}
              icon={<DocumentIcon color={brandColor} w="24px" h="24px" />}
            />
          }
          name="Categories"
          value={categories.length}
        />
      </SimpleGrid>

      <SimpleGrid columns={{ base: 1, md: 2, xl: 2 }} gap="20px" mb="20px">
        <FinanceTotalSpent />
        <FinanceMonthlyRevenue />
      </SimpleGrid>

      <SimpleGrid columns={{ base: 1, md: 1, xl: 2 }} gap="20px" mb="20px">
        <FinanceRecentTable />
        <SimpleGrid columns={{ base: 1, md: 2, xl: 2 }} gap="20px">
          <FinanceDailyExpenses />
          <ExpensePieCard />
        </SimpleGrid>
      </SimpleGrid>
    </Box>
  );
}
